import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Loader2, ChevronDown } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DailyLeaderReport = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedReport, setExpandedReport] = useState(null);

  useEffect(() => {
    loadReports();
  }, []);

  const loadReports = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API}/daily-leader/weekly-reports`);
      setReports(data || []);
      if (data && data.length > 0) setExpandedReport(data[0].id);
    } catch (e) {} finally { setLoading(false); }
  };

  const formatMoney = (v) => `$${(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-blue-600" /></div>;

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-bold" data-testid="report-title">Relatórios Semanais</h2>

      {reports.length === 0 ? (
        <p className="text-center text-gray-500 py-8">Nenhum relatório encontrado</p>
      ) : (
        <div className="space-y-3">
          {reports.map(r => {
            const isExpanded = expandedReport === r.id;
            return (
              <Card key={r.id} data-testid={`report-card-${r.semana}-${r.ano}`}>
                <CardHeader className="pb-2 cursor-pointer" onClick={() => setExpandedReport(isExpanded ? null : r.id)}>
                  <CardTitle className="text-base flex items-center justify-between">
                    <span>Semana {r.semana}/{r.ano}</span>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-xs">{r.data_inicio} - {r.data_fim}</Badge>
                      <ChevronDown className={`h-4 w-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                    </div>
                  </CardTitle>
                </CardHeader>
                {isExpanded && (
                <CardContent className="space-y-3">
                  {/* Líder */}
                  <div className="bg-white rounded-lg border border-blue-200 p-3">
                    <h4 className="font-semibold text-gray-800 mb-2 text-sm">👨‍💼 Líder</h4>
                    <div className="flex justify-between py-1.5 border-b border-gray-100">
                      <span className="text-sm text-gray-600">Dias Trabalhados</span>
                      <span className="font-bold">{r.dias_trabalhados}</span>
                    </div>
                    <div className="flex justify-between py-1.5 border-b border-gray-100">
                      <span className="text-sm text-gray-600">Diária</span>
                      <span className="font-bold">{formatMoney(r.valor_diaria)}</span>
                    </div>
                    <div className="flex justify-between py-1.5 border-b border-gray-100">
                      <span className="text-sm text-gray-600">Subtotal Líder</span>
                      <span className="font-bold text-blue-600">{formatMoney(r.valor_trabalho)}</span>
                    </div>

                    {/* Helpers Fixos */}
                    {r.helpers && r.helpers.length > 0 && (
                      <>
                        <h4 className="font-semibold text-gray-800 mt-3 mb-1.5 text-sm">👷 Helpers</h4>
                        {r.helpers.map((h, i) => (
                          <div key={i} className="py-1.5 border-b border-gray-100">
                            <div className="flex justify-between items-center">
                              <span className="text-sm text-gray-700 font-medium">{h.nome}</span>
                              <span className="font-bold text-green-600">{formatMoney(h.total)}</span>
                            </div>
                            <div className="text-xs text-gray-500 mt-0.5">
                              {h.dias} dias × {formatMoney(h.diaria)}/dia
                            </div>
                          </div>
                        ))}
                      </>
                    )}

                    {/* Helpers Avulsos */}
                    {r.helpers_avulsos && r.helpers_avulsos.length > 0 && (
                      <>
                        <h4 className="font-semibold text-gray-800 mt-3 mb-1.5 text-sm">🔧 Helpers Avulsos</h4>
                        {r.helpers_avulsos.map((h, i) => (
                          <div key={i} className="py-1.5 border-b border-gray-100">
                            <div className="flex justify-between items-center">
                              <span className="text-sm text-gray-700 font-medium">{h.nome}</span>
                              <span className="font-bold text-orange-500">{formatMoney(h.total)}</span>
                            </div>
                            <div className="text-xs text-gray-500 mt-0.5">
                              {h.dias} dias × {formatMoney(h.diaria)}/dia
                            </div>
                          </div>
                        ))}
                      </>
                    )}

                    {/* Despesas */}
                    <div className="flex justify-between py-1.5 border-b border-gray-100 mt-2">
                      <span className="text-sm text-gray-600 font-medium">Despesas</span>
                      <span className="font-bold text-orange-600">{formatMoney(r.valor_despesas)}</span>
                    </div>
                  </div>

                  {/* Total */}
                  <div className="bg-gradient-to-r from-green-100 to-emerald-100 rounded-lg p-3 border-2 border-green-400">
                    <div className="flex justify-between items-center">
                      <span className="font-bold text-gray-800 text-lg">TOTAL:</span>
                      <span className="font-bold text-green-700 text-xl">{formatMoney(r.valor_total)}</span>
                    </div>
                    <div className="text-xs text-gray-600 mt-1">
                      Líder: {formatMoney(r.valor_trabalho)}
                      {r.helpers_total > 0 ? ` + Helpers: ${formatMoney(r.helpers_total)}` : ''}
                      {r.valor_despesas > 0 ? ` + Despesas: ${formatMoney(r.valor_despesas)}` : ''}
                    </div>
                  </div>
                </CardContent>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default DailyLeaderReport;
